import React from 'react';
import { Link } from 'react-router-dom';
import { usePageChat } from '@/hooks';
import { ChatInput } from '../components/chat/ChatInput';
import { QuickSuggestionsBlock } from '../components/chat/QuickSuggestionsBlock';
import { Header } from '@components/layout';
import { Footer } from '@components/layout';
import { ChatContainer } from '../components/chat/ChatContainer';
import { LoadingState } from '../components/common/LoadingState';
import { SchemaMarkup } from '@/components/common/SchemaMarkup';
import { CanonicalTag } from '@/components/common/CanonicalTag';
import { getResearch } from '@/config/research.config';

interface ResearchIndexPageProps {
    showBanner: () => void;
}

const faqItems = [
    {
        question: 'How do I research a vehicle before buying?',
        answer: 'Start by identifying your priorities — family space, fuel economy, towing capability, or budget. Then compare models within your category and read reliability data before visiting a dealership.',
    },
    {
        question: 'What is the most reliable vehicle to buy used?',
        answer: 'Honda and Toyota vehicles consistently rank among the most reliable used vehicles. Models like the Honda CR-V, Honda Civic, Toyota RAV4, and Toyota Corolla are known for long-term reliability.',
    },
    {
        question: 'What SUV is best for families?',
        answer: 'Family SUVs are often evaluated on seating capacity, cargo space, safety ratings, and fuel economy. The Honda CR-V, Toyota RAV4, Hyundai Santa Fe, and Nissan Rogue are commonly recommended for families.',
    },
    {
        question: 'What truck is best for towing?',
        answer: 'The Ford F-150 and Chevrolet Silverado are among the most capable trucks for towing. Towing capacity, engine options, and payload ratings are key factors when comparing trucks.',
    },
];

export const ResearchIndexPage: React.FC<ResearchIndexPageProps> = ({ showBanner }) => {
    const { messages, isLoading, streamingMessage, sendMessage, assistant, loadingAssistants, messagesEndRef, currentTool } = usePageChat();
    const allResearch = getResearch();

    const chatSuggestions = [
        'Show me SUVs available near me',
        'Find reliable used vehicles near me',
        'Compare trucks near me',
        'Browse family SUVs near me',
    ];

    const faqSchema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqItems.map(item => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: { '@type': 'Answer', text: item.answer },
        })),
    };

    if (loadingAssistants || !assistant) {
        return (
            <>
                <Header />
                <div className="pt-20 sm:pt-24 md:pt-28 pb-28 px-2 sm:px-4">
                    <div className="max-w-3xl mx-auto"><LoadingState message="Loading..." /></div>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <CanonicalTag href="https://getdrivesource.com/research" />
            <SchemaMarkup schema={faqSchema} />
            <Header />
            <div className="pt-20 sm:pt-24 md:pt-28 pb-28 sm:pb-32 md:pb-36 px-2 sm:px-3 md:px-4">
                <div className="max-w-3xl mx-auto">
                    <div className="min-h-[60vh] space-y-2 sm:space-y-3 md:space-y-4 py-2 sm:py-3 md:py-4">
                        <article className="mb-6">

                            {/* H1 */}
                            <h1 className="text-lg sm:text-2xl md:text-3xl font-bold text-white mb-2">
                                Vehicle Research Guides
                                <span className="block text-sm sm:text-base md:text-lg font-medium text-gray-400 mt-1">
                                    Research SUVs, Trucks, and Cars Before You Buy
                                </span>
                            </h1>

                            {/* Intro */}
                            <div className="mb-4">
                                <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                    Research Vehicles to Make a More Confident Decision
                                </h2>
                                <p className="text-sm sm:text-base text-gray-300 mb-3">
                                    Researching vehicles before visiting a dealership helps drivers understand which models best fit their needs. These guides cover reliability, fuel economy, towing capability, and long-term ownership to help you compare options.
                                </p>
                                <p className="text-sm sm:text-base text-gray-300 mb-2">
                                    Common research topics include:
                                </p>
                                <ul className="list-disc list-inside space-y-1.5 text-sm sm:text-base text-gray-300 ml-2">
                                    <li>Best SUVs for families and passenger space</li>
                                    <li>Most reliable used vehicles for long-term ownership</li>
                                    <li>Best trucks for towing and payload capacity</li>
                                    <li>SUVs with the best winter driving capability</li>
                                    <li>Affordable SUVs under $30,000</li>
                                </ul>
                            </div>

                            {/* Research Guides List */}
                            {allResearch.length > 0 && (
                                <div className="mb-4">
                                    <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                        Vehicle Research Guides
                                    </h2>
                                    <ul className="list-disc list-inside space-y-1.5 sm:space-y-2 text-sm sm:text-base text-gray-300 ml-2">
                                        {allResearch.map((r, i) => {
                                            const [main] = r.h1.includes(' — ') ? r.h1.split(' — ') : [r.h1];
                                            return (
                                                <li key={i}>
                                                    <Link
                                                        to={`/research/${r.slug}`}
                                                        className="text-cyan-400 hover:text-cyan-300 underline transition-colors"
                                                    >
                                                        {main}
                                                    </Link>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            )}

                            {/* Why Research */}
                            <div className="mb-4">
                                <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                    Why Research Vehicles Before Visiting a Dealership
                                </h2>
                                <p className="text-sm sm:text-base text-gray-300 mb-2">
                                    Drivers who research vehicles beforehand are better prepared to evaluate their options and ask the right questions. Vehicle research typically covers:
                                </p>
                                <ul className="list-disc list-inside space-y-1 text-sm sm:text-base text-gray-300 ml-2">
                                    <li>reliability history and long-term ownership costs</li>
                                    <li>fuel economy and operating expenses</li>
                                    <li>safety ratings and available features</li>
                                    <li>how vehicles compare within the same category</li>
                                    <li>which model best fits a specific use case</li>
                                </ul>
                            </div>

                            {/* FAQ */}
                            <div className="mb-4">
                                <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                    Frequently Asked Questions About Vehicle Research
                                </h2>
                                <div className="space-y-4">
                                    {faqItems.map((item, i) => (
                                        <div key={i}>
                                            <h3 className="text-sm sm:text-base font-semibold text-gray-200 mb-1">{item.question}</h3>
                                            <p className="text-sm sm:text-base text-gray-300">{item.answer}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Chat CTA */}
                            <QuickSuggestionsBlock
                                topLine="Ready to find your vehicle?"
                                title="Find Vehicles Near You"
                                suggestions={chatSuggestions}
                                onSend={sendMessage}
                            />

                        </article>

                        {messages.length > 0 && (
                            <ChatContainer
                                messages={messages}
                                assistant={assistant}
                                isLoading={isLoading}
                                streamingMessage={streamingMessage}
                                currentTool={currentTool}
                            />
                        )}
                        <div ref={messagesEndRef} />
                    </div>
                </div>
            </div>
            <ChatInput
                assistant={assistant}
                onSendMessage={sendMessage}
                disabled={isLoading}
                showBanner={showBanner}
            />
        </>
    );
};
